import {
    AddOutlined,
    Cancel,
    InfoOutlined,
    KeyboardArrowDown,
} from "@mui/icons-material";
import {
    Divider,
    Grid,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import {
    DateRangeFilter,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents";
import ApplicationFilterItem from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/ApplicationFilterItem";
import type { StatusDescriptor } from "components/UI/StatusTags";
import FilterBuilderAddFilterSelect, {
    type FilterBuilderAddFilterGroup,
    type FilterBuilderAddFilterOption,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/FilterBuilderAddFilterSelect";
import AcxButton from "components/UI/AcxButton";
import AcxInputLabel from "components/UI/AcxInputLabel";
import moment from "moment";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type {
    IApplicationFilterItem,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import BaseObjectStore from "../BaseObjectStore";
import {
    OBJECT_CALL_DURATION_FILTER_KEY,
    isExtendedMetadataObjectFilterKey,
    OBJECT_DATE_TYPE_FILTER_KEY,
    OBJECT_MESSAGE_COUNT_FILTER_KEY,
    OBJECT_SELECTABLE_FILTER_KEYS,
    type ObjectFilterKey,
    type ObjectSelectableFilterKey,
} from ".";
import ObjectSavedFiltersSelector from "./ObjectSavedFiltersSelector";
import theme from "Theme/AppTheme";

type Props = {
    store: BaseObjectStore<any, any>;
    sourceFiltersStore?: ApplicationFiltersInstanceStore;
    sourceDateFilters?:
        | Pick<
              ApplicationFilters,
              "startDate" | "endDate" | "dateRangeLabel" | "dateReferenceOption"
          >
        | null;
};

type AddObjectFilterOption = IApplicationFilterItem &
    FilterBuilderAddFilterOption & {
    filterKey: ObjectSelectableFilterKey;
};

type AddObjectFilterGroup = FilterBuilderAddFilterGroup<AddObjectFilterOption>;

const configureInputLabelSx = {
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: "12px",
    lineHeight: "16px",
};

function getCallDurationRangeForObjectFilter(
    filters:
        | Pick<ApplicationFilters, "minCallDuration" | "maxCallDuration">
        | null
        | undefined,
): [number, number] {
    const min = filters?.minCallDuration ?? 0;
    const max = filters?.maxCallDuration ?? 0;

    if (min <= 0 && max <= 0) {
        return [0, 0];
    }

    return [min, max];
}

function getMessageCountRangeForObjectFilter(
    filters:
        | Pick<ApplicationFilters, "minMessageCount" | "maxMessageCount">
        | null
        | undefined,
): [number, number] {
    const min = filters?.minMessageCount ?? 0;
    const max = filters?.maxMessageCount ?? 0;

    if (min <= 0 && max <= 0) {
        return [0, 0];
    }

    return [min, max];
}

/** Type guard for filters selectable in object-level filter builder UI. */
function isObjectFilterKey(
    filterKey: IApplicationFilterItem["filterKey"],
): filterKey is ObjectSelectableFilterKey {
    return (
        (OBJECT_SELECTABLE_FILTER_KEYS as readonly string[]).includes(
            filterKey,
        ) || isExtendedMetadataObjectFilterKey(filterKey)
    );
}

/** Returns true for filter keys that should be evaluated against source conflicts. */
function isComparableFilterKey(
    filterKey: ObjectSelectableFilterKey,
): filterKey is ObjectFilterKey {
    return filterKey !== OBJECT_DATE_TYPE_FILTER_KEY;
}

/** Renders the object-level filter builder and source-restriction-aware controls. */
const ObjectFiltersSection: React.FC<Props> = observer(
    ({ store, sourceFiltersStore, sourceDateFilters }) => {
        const applicationFiltersStore = useStore(ApplicationFiltersStore);
        const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
        const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
        const sourceStore = sourceFiltersStore ?? applicationFiltersStore;
        const inferredDateSourceStore =
            sourceFiltersStore instanceof ApplicationFiltersStore
                ? sourceFiltersStore.quickFiltersStore
                : sourceFiltersStore ??
                  applicationFiltersStore.quickFiltersStore ??
                  applicationFiltersStore;
        const sourceDateStore =
            sourceDateFilters === undefined
                ? inferredDateSourceStore
                : sourceDateFilters;
        const hasSourceDateStore = sourceDateStore !== null;
        const objectFiltersStore = store.objectFiltersStore;
        const isDateFilterDisabled = store.isDateFilterDisabled;
        const sourceCallDurationRange = getCallDurationRangeForObjectFilter(
            sourceStore,
        );
        const sourceMessageCountRange = getMessageCountRangeForObjectFilter(
            sourceStore,
        );
        const comparableFilterKeys = useMemo<ObjectFilterKey[]>(() => {
            const keySet = new Set<ObjectFilterKey>();
            applicationFilterItemsStore.filterGroups.forEach((group) => {
                group.items.forEach((item) => {
                    if (!isObjectFilterKey(item.filterKey)) return;
                    if (!isComparableFilterKey(item.filterKey)) return;
                    keySet.add(item.filterKey);
                });
            });
            return Array.from(keySet);
        }, [applicationFilterItemsStore.filterGroups]);
        const comparisonByKey = objectFiltersStore
            ? sourceStore.compareFiltersByKey(
                  objectFiltersStore,
                  comparableFilterKeys,
                  { agents: applicationFilterValuesStore.agents },
              ).byKey
            : undefined;
        const [selectedFilterKeys, setSelectedFilterKeys] = useState<
            ObjectSelectableFilterKey[]
        >([]);
        const [savedFilterSectionVisible, setSavedFilterSectionVisible] =
            useState(false);
        const dateRangeSelectionFromPresetRef = React.useRef(false);
        const [isSelectFilterVisible, setIsSelectFilterVisible] =
            useState(false);
        const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);

        const syncSelectedFilterKeysFromStore = useCallback(() => {
            if (!objectFiltersStore) {
                setSelectedFilterKeys([]);
                return;
            }

            const nextKeys: ObjectSelectableFilterKey[] = Array.from(
                new Set<ObjectSelectableFilterKey>(
                    applicationFilterItemsStore
                        .getAppliedFilterItems(objectFiltersStore)
                        .filter((item) =>
                            applicationFilterItemsStore.canAccessFilterItem(
                                item,
                            ),
                        )
                        .map((item) => item.filterKey)
                        .filter((key) => isObjectFilterKey(key))
                        .filter((key) => key !== OBJECT_DATE_TYPE_FILTER_KEY),
                ),
            );

            if (store.objectFilterDateTypeOverride) {
                nextKeys.push(OBJECT_DATE_TYPE_FILTER_KEY);
            }
            if (
                store.objectFilterCallDurationOverride &&
                !nextKeys.includes(OBJECT_CALL_DURATION_FILTER_KEY)
            ) {
                nextKeys.push(OBJECT_CALL_DURATION_FILTER_KEY);
            }
            if (
                store.objectFilterMessageCountOverride &&
                !nextKeys.includes(OBJECT_MESSAGE_COUNT_FILTER_KEY)
            ) {
                nextKeys.push(OBJECT_MESSAGE_COUNT_FILTER_KEY);
            }

            setSelectedFilterKeys(nextKeys);
        }, [
            applicationFilterItemsStore,
            objectFiltersStore,
            store.objectFilterCallDurationOverride,
            store.objectFilterDateTypeOverride,
            store.objectFilterMessageCountOverride,
        ]);

        const filterTitleMap = useMemo(() => {
            const titles = new Map<ObjectSelectableFilterKey, string>();
            applicationFilterItemsStore.filterGroups.forEach((group) => {
                group.items.forEach((item) => {
                    if (
                        isObjectFilterKey(item.filterKey) &&
                        applicationFilterItemsStore.canAccessFilterItem(item)
                    ) {
                        titles.set(
                            item.filterKey as ObjectSelectableFilterKey,
                            item.title,
                        );
                    }
                });
            });

            return titles;
        }, [applicationFilterItemsStore]);

        // The status tag travels with the title so a chart-level filter advertises the same
        // Deprecated/New state the filter picker does. Without this, the two identically-titled
        // Theme filters would be indistinguishable here.
        const filterStatusMap = useMemo(() => {
            const statuses = new Map<
                ObjectSelectableFilterKey,
                StatusDescriptor
            >();
            applicationFilterItemsStore.filterGroups.forEach((group) => {
                group.items.forEach((item) => {
                    if (
                        item.status &&
                        isObjectFilterKey(item.filterKey) &&
                        applicationFilterItemsStore.canAccessFilterItem(item)
                    ) {
                        statuses.set(
                            item.filterKey as ObjectSelectableFilterKey,
                            item.status,
                        );
                    }
                });
            });

            return statuses;
        }, [applicationFilterItemsStore]);

        /** Converts an unknown date-like value to epoch milliseconds for comparison. */
        const normalizeDate = useCallback((value: unknown) => {
            if (!value) return null;
            const parsedDate = moment(value as moment.MomentInput);
            if (!parsedDate.isValid()) return null;
            return parsedDate.valueOf();
        }, []);

        const doesDateRangeMatchSource = useCallback(
            (
                filters:
                    | Pick<
                          ApplicationFilters,
                          "startDate" | "endDate" | "dateRangeLabel"
                      >
                    | null
                    | undefined,
            ) => {
                if (!hasSourceDateStore || !filters) return false;

                const candidateStartDate = normalizeDate(filters.startDate);
                const sourceStartDate = normalizeDate(sourceDateStore.startDate);
                const candidateEndDate = normalizeDate(filters.endDate);
                const sourceEndDate = normalizeDate(sourceDateStore.endDate);
                const labelsMatch =
                    (filters.dateRangeLabel ?? "") ===
                    (sourceDateStore.dateRangeLabel ?? "");

                return (
                    candidateStartDate === sourceStartDate &&
                    candidateEndDate === sourceEndDate &&
                    labelsMatch
                );
            },
            [
                hasSourceDateStore,
                normalizeDate,
                sourceDateStore?.dateRangeLabel,
                sourceDateStore?.endDate,
                sourceDateStore?.startDate,
            ],
        );

        /** Returns true when object-level date range exactly matches the source range. */
        const isDateRangeMatchingSource = useCallback(() => {
            if (!objectFiltersStore) return true;
            return doesDateRangeMatchSource(objectFiltersStore);
        }, [doesDateRangeMatchSource, objectFiltersStore]);

        useEffect(() => {
            syncSelectedFilterKeysFromStore();
            setIsSelectFilterVisible(false);
        }, [
            applicationFilterItemsStore.extendedMetadataFields,
            applicationFilterItemsStore.filterGroups,
            syncSelectedFilterKeysFromStore,
            store.id,
        ]);

        useEffect(() => {
            if (!objectFiltersStore || store.objectFilterDateOverride) return;
            if (!hasSourceDateStore) return;
            if (isDateRangeMatchingSource()) return;

            objectFiltersStore.setStartDate(sourceDateStore.startDate);
            objectFiltersStore.setEndDate(sourceDateStore.endDate);
            objectFiltersStore.setDateRangeLabel(
                sourceDateStore.dateRangeLabel ?? "",
            );
        }, [
            hasSourceDateStore,
            isDateRangeMatchingSource,
            objectFiltersStore,
            objectFiltersStore?.dateRangeLabel,
            objectFiltersStore?.endDate,
            objectFiltersStore?.startDate,
            sourceDateStore?.dateRangeLabel,
            sourceDateStore?.endDate,
            sourceDateStore?.startDate,
            store.objectFilterDateOverride,
        ]);

        useEffect(() => {
            if (!objectFiltersStore || !hasSourceDateStore) return;
            if (store.objectFilterDateTypeOverride) return;

            const sourceDateType = sourceDateStore?.dateReferenceOption;
            if (
                sourceDateType === undefined ||
                objectFiltersStore.dateReferenceOption === sourceDateType
            ) {
                return;
            }

            objectFiltersStore.setDateReferenceOption(sourceDateType);
        }, [
            hasSourceDateStore,
            objectFiltersStore,
            objectFiltersStore?.dateReferenceOption,
            sourceDateStore?.dateReferenceOption,
            store.objectFilterDateTypeOverride,
        ]);

        const addableFilterGroups = useMemo<AddObjectFilterGroup[]>(() => {
            // Same Legacy bucketing the filter picker uses, so a chart-level filter is filed the
            // way the dashboard files it.
            return applicationFilterItemsStore
                .withLegacyGrouping(applicationFilterItemsStore.filterGroups)
                .map((group) => {
                    const options = group.items
                        .filter((item) => isObjectFilterKey(item.filterKey))
                        .filter((item) =>
                            applicationFilterItemsStore.canAccessFilterItem(
                                item,
                            ),
                        )
                        .filter(
                            (item) =>
                                !selectedFilterKeys.includes(
                                    item.filterKey as ObjectSelectableFilterKey,
                                ),
                        )
                        .map((item) => {
                            const key =
                                item.filterKey as ObjectSelectableFilterKey;
                            const comparison = isComparableFilterKey(key)
                                ? comparisonByKey?.[key]
                                : undefined;
                            const isDisabledByCard =
                                key === OBJECT_DATE_TYPE_FILTER_KEY &&
                                isDateFilterDisabled;
                            const isDisabledBySource =
                                comparison?.disabledAtObject ?? false;
                            const isDisabled =
                                isDisabledByCard || isDisabledBySource;

                            const option: AddObjectFilterOption = {
                                ...item,
                                filterKey: key,
                                isDisabled,
                                disabledLabel: isDisabledByCard
                                    ? "Not available for this card"
                                    : isDisabledBySource
                                      ? "Selected in dashboard filters"
                                      : undefined,
                            };

                            return option;
                        });

                    return {
                        label: group.title,
                        options,
                    };
                })
                .filter((group) => group.options.length > 0);
        }, [
            comparisonByKey,
            applicationFilterItemsStore,
            isDateFilterDisabled,
            selectedFilterKeys,
        ]);

        if (!objectFiltersStore) return null;

        const isSavedFilterActionAvailable =
            applicationFiltersStore.getSavedFiltersByDomains(
                ApplicationFiltersStore.AllSavedFilterDomains,
            ).length > 0;

        /** Removes an object-level filter key and clears related override flags. */
        const removeFilterKey = (key: ObjectSelectableFilterKey) => {
            objectFiltersStore.resetByKey(key);
            if (key === OBJECT_DATE_TYPE_FILTER_KEY) {
                store.objectFilterDateTypeOverride = false;
            }
            if (key === OBJECT_CALL_DURATION_FILTER_KEY) {
                store.objectFilterCallDurationOverride = false;
            }
            if (key === OBJECT_MESSAGE_COUNT_FILTER_KEY) {
                store.objectFilterMessageCountOverride = false;
            }
            setSelectedFilterKeys((prev) => {
                const next = prev.filter((item) => item !== key);
                if (next.length === 0) setIsSelectFilterVisible(false);
                return next;
            });
        };

        /** Adds a filter key to the builder, enabling date-type override when applicable. */
        const addFilterKey = (key: ObjectSelectableFilterKey) => {
            if (selectedFilterKeys.includes(key)) return;
            if (key === OBJECT_DATE_TYPE_FILTER_KEY) {
                store.objectFilterDateTypeOverride = true;
            }
            if (key === OBJECT_CALL_DURATION_FILTER_KEY) {
                store.objectFilterCallDurationOverride = true;
                objectFiltersStore.setCallDuration(sourceCallDurationRange);
            }
            if (key === OBJECT_MESSAGE_COUNT_FILTER_KEY) {
                store.objectFilterMessageCountOverride = true;
                objectFiltersStore.setMessageCount(sourceMessageCountRange);
            }
            setSelectedFilterKeys((prev) => [...prev, key]);
            setIsSelectFilterVisible(false);
        };

        /** Copies source date range values into the object-level filter store. */
        const syncObjectDateRangeToDashboard = () => {
            if (!hasSourceDateStore) return;
            objectFiltersStore.setStartDate(sourceDateStore.startDate);
            objectFiltersStore.setEndDate(sourceDateStore.endDate);
            objectFiltersStore.setDateRangeLabel(
                sourceDateStore.dateRangeLabel ?? "",
            );
        };

        /** Alias for readability where dashboard wording is preferred in UI handlers. */
        const isDateRangeMatchingDashboard = () => {
            return isDateRangeMatchingSource();
        };

        /** Updates the date override toggle based on whether ranges diverge. */
        const setDateOverrideFromSelection = () => {
            store.objectFilterDateOverride = !isDateRangeMatchingDashboard();
        };

        /** Clears the date override and re-syncs date range to source filters. */
        const clearDateOverride = () => {
            store.objectFilterDateOverride = false;
            syncObjectDateRangeToDashboard();
        };

        const removeSavedFilterSection = () => {
            setSavedFilterSectionVisible(false);
            setIsSelectFilterVisible(false);
        };

        const showSavedFilterSection = () => {
            objectFiltersStore.resetFilters();
            store.objectFilterDateOverride = false;
            store.objectFilterDateTypeOverride = false;
            store.objectFilterCallDurationOverride = false;
            store.objectFilterMessageCountOverride = false;
            setSelectedFilterKeys([]);
            setSavedFilterSectionVisible(true);
            setIsSelectFilterVisible(false);
        };

        /** Renders one object-level filter item row with source-driven restrictions. */
        const renderFilterItem = (key: ObjectSelectableFilterKey) => {
            const comparison = isComparableFilterKey(key)
                ? comparisonByKey?.[key]
                : undefined;
            const optionRestriction = comparison?.optionRestriction;
            const restrictedValues =
                optionRestriction?.type === "allowOnly"
                    ? optionRestriction.values
                    : undefined;
            const hasOptionRestrictions =
                (optionRestriction?.values?.length ?? 0) > 0;
            const isDisabledBySource = comparison?.disabledAtObject ?? false;
            const title = filterTitleMap.get(key) ?? key;
            const status = filterStatusMap.get(key);

            return (
                <Grid item key={key}>
                    <Grid
                        container
                        direction="column"
                        sx={{
                            py: 0.5,
                            opacity: isDisabledBySource ? 0.6 : 1,
                            "& .object-filter-editor-shell": {
                                pointerEvents: isDisabledBySource
                                    ? "none"
                                    : "auto",
                            },
                            "& .object-filter-remove-slot": {
                                width: 0,
                                marginLeft: 0,
                                overflow: "hidden",
                                transition:
                                    "width 120ms ease, margin-left 120ms ease",
                            },
                            "&:hover .object-filter-remove-slot": {
                                width: "24px",
                                marginLeft: 1,
                            },
                        }}
                    >
                        <Grid container alignItems="center" wrap="nowrap">
                            <Grid item xs className="object-filter-editor-shell">
                                <ApplicationFilterItem
                                    title={title}
                                    status={status}
                                    filterKey={key}
                                    store={objectFiltersStore}
                                    instanceStore={objectFiltersStore}
                                    isClearable={false}
                                    forceFullWidth
                                    optionRestriction={optionRestriction}
                                    modeRestriction={comparison?.modeRestriction}
                                    rangeRestriction={comparison?.rangeRestriction}
                                    moduleScoreRestriction={
                                        comparison?.moduleScoreRestriction
                                    }
                                    restrictedValues={restrictedValues}
                                />
                            </Grid>
                            <Grid item className="object-filter-remove-slot">
                                <IconButton
                                    id={`remove-object-filter-${key}`}
                                    size="small"
                                    onClick={() => removeFilterKey(key)}
                                    sx={{
                                        p: 0.125,
                                    }}
                                >
                                    <Cancel
                                        fontSize="small"
                                        htmlColor={theme.palette.gray[400]}
                                    />
                                </IconButton>
                            </Grid>
                        </Grid>
                        {hasOptionRestrictions && (
                            <Grid
                                container
                                item
                                alignItems="center"
                                wrap="nowrap"
                                columnGap={0.75}
                                sx={{ mt: 0.75 }}
                            >
                                <Grid item>
                                    <InfoOutlined
                                        sx={{
                                            fontSize: 18,
                                            color: theme.palette.blue.main,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.blue.main,
                                        }}
                                    >
                                        Options limited by dashboard-level
                                        selections.
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            );
        };

        const showAddFilterDropdown =
            !savedFilterSectionVisible && isSelectFilterVisible;
        const renderedSectionCount =
            selectedFilterKeys.length + (savedFilterSectionVisible ? 1 : 0);
        const showAndDividerForAddSection = renderedSectionCount > 0;
        const showAddSectionDivider =
            !savedFilterSectionVisible &&
            (renderedSectionCount > 0 || isSelectFilterVisible);
        const canOpenAddFilterSelector =
            !savedFilterSectionVisible &&
            (addableFilterGroups.length > 0 || isSavedFilterActionAvailable);
        const shouldRenderAddFilterSelect =
            showAddFilterDropdown &&
            (addableFilterGroups.length > 0 || isSavedFilterActionAvailable);

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <AcxInputLabel
                        labelText="Date Range"
                        sx={configureInputLabelSx}
                    />
                    <Grid container direction="column" rowGap={1}>
                        <Grid item xs={12}>
                            <DateRangeFilter
                                store={objectFiltersStore}
                                filterKey="dateRange"
                                maxWidth="100%"
                                wrapperStyle={{
                                    maxWidth: "100%",
                                    minWidth: "100%",
                                }}
                                inputStyle={{ width: "100%" }}
                                allowManualDateSelectionWithLabel
                                viewOnly={isDateFilterDisabled}
                                showClearButton={store.objectFilterDateOverride}
                                onClear={clearDateOverride}
                                onSelect={() => {
                                    if (dateRangeSelectionFromPresetRef.current) {
                                        dateRangeSelectionFromPresetRef.current =
                                            false;
                                        setDateOverrideFromSelection();
                                        return;
                                    }

                                    setDateOverrideFromSelection();

                                    if (
                                        store.objectFilterDateOverride &&
                                        objectFiltersStore.dateRangeLabel
                                    ) {
                                        objectFiltersStore.setDateRangeLabel("");
                                    }
                                }}
                                onDateRangeLabelChange={(dateRangeLabel) => {
                                    dateRangeSelectionFromPresetRef.current =
                                        !!dateRangeLabel;
                                    setDateOverrideFromSelection();
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {store.objectFilterDateOverride ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    wrap="nowrap"
                                    columnGap={0.75}
                                >
                                    <Grid item>
                                        <InfoOutlined
                                            sx={{
                                                fontSize: 18,
                                                color: theme.palette.blue.main,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.blue.main,
                                            }}
                                        >
                                            Selected date range will override
                                            the dashboard date range.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.gray[700] }}
                                >
                                    {isDateFilterDisabled
                                        ? "Date range always matches the dashboard for this card."
                                        : "Date range is currently matching the dashboard."}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Paper
                        sx={{
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.gray[200]}`,
                            boxShadow: "none",
                            overflow: "hidden",
                        }}
                    >
                        <Grid
                            container
                            item
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                px: 2,
                                py: 1.25,
                                borderBottom: `1px solid ${theme.palette.gray[200]}`,
                            }}
                        >
                            <Grid item>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ color: theme.palette.gray[700] }}
                                >
                                    Filters
                                </Typography>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    id={`${store.id}-toggle-object-filters-builder`}
                                    size="small"
                                    onClick={() =>
                                        setIsFiltersExpanded((open) => !open)
                                    }
                                    sx={{ p: 0.25 }}
                                >
                                    <KeyboardArrowDown
                                        sx={{
                                            color: theme.palette.gray[400],
                                            transition:
                                                "transform 150ms ease",
                                            transform: isFiltersExpanded
                                                ? "rotate(0deg)"
                                                : "rotate(-90deg)",
                                        }}
                                    />
                                </IconButton>
                            </Grid>
                        </Grid>

                        {isFiltersExpanded && (
                            <Grid
                                container
                                item
                                direction="column"
                                rowGap={1.5}
                                sx={{ p: 1.5 }}
                            >
                                {selectedFilterKeys.map((key, index) => (
                                    <React.Fragment key={`object-filter-${key}`}>
                                        {index > 0 && (
                                            <Grid
                                                container
                                                item
                                                alignItems="center"
                                                columnGap={1}
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette
                                                                .gray[400],
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        AND
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs>
                                                    <Divider />
                                                </Grid>
                                            </Grid>
                                        )}
                                        {renderFilterItem(key)}
                                    </React.Fragment>
                                ))}

                                {savedFilterSectionVisible &&
                                    selectedFilterKeys.length > 0 && (
                                        <Grid
                                            container
                                            item
                                            alignItems="center"
                                            columnGap={1}
                                        >
                                            <Grid item>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette
                                                            .gray[400],
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    AND
                                                </Typography>
                                            </Grid>
                                            <Grid item xs>
                                                <Divider />
                                            </Grid>
                                        </Grid>
                                    )}

                                <ObjectSavedFiltersSelector
                                    isVisible={savedFilterSectionVisible}
                                    store={store}
                                    objectFiltersStore={objectFiltersStore}
                                    sourceStore={sourceStore}
                                    sourceDateStore={sourceDateStore}
                                    onRemove={removeSavedFilterSection}
                                    onApplied={() => {
                                        setSavedFilterSectionVisible(false);
                                        setIsSelectFilterVisible(false);
                                    }}
                                    syncSelectedFilterKeysFromStore={
                                        syncSelectedFilterKeysFromStore
                                    }
                                />

                                {showAddSectionDivider && (
                                    <Grid
                                        container
                                        item
                                        alignItems="center"
                                        columnGap={1}
                                    >
                                        {showAndDividerForAddSection && (
                                            <Grid item>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette
                                                            .gray[400],
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    AND
                                                </Typography>
                                            </Grid>
                                        )}
                                        <Grid item xs>
                                            <Divider />
                                        </Grid>
                                    </Grid>
                                )}

                                {shouldRenderAddFilterSelect && (
                                    <Grid item>
                                        <FilterBuilderAddFilterSelect<AddObjectFilterOption>
                                            id={`${store.id}-add-object-filter`}
                                            options={addableFilterGroups}
                                            onChange={(option) => {
                                                if (!option) return;
                                                addFilterKey(
                                                    option.filterKey as ObjectSelectableFilterKey,
                                                );
                                            }}
                                            footerAction={{
                                                id: `${store.id}-add-saved-filter-footer`,
                                                label: "Apply Saved Filter",
                                                onClick: showSavedFilterSection,
                                            }}
                                        />
                                    </Grid>
                                )}

                                {!savedFilterSectionVisible && (
                                    <Grid item>
                                        <AcxButton
                                            id={`${store.id}-add-filter-button`}
                                            color="primary"
                                            onClick={() =>
                                                setIsSelectFilterVisible(true)
                                            }
                                            disabled={!canOpenAddFilterSelector}
                                            sx={(theme) => ({
                                                color: theme.palette.blue.main,
                                                backgroundColor:
                                                    theme.palette.white.main,
                                                paddingInline: theme.spacing(2),
                                                paddingBlock: theme.spacing(0.75),
                                                borderRadius: "40px",
                                                border: `1px solid ${theme.palette.gray[200]}`,
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                lineHeight: "20px",
                                                ":hover": {
                                                    backgroundColor:
                                                        theme.palette.white.main,
                                                    opacity: "85%",
                                                },
                                            })}
                                            startIcon={
                                                <AddOutlined
                                                    sx={{ fontSize: "20px" }}
                                                />
                                            }
                                        >
                                            Filter
                                        </AcxButton>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        );
    },
);

export default ObjectFiltersSection;
